import { trackDeliveryExperiment } from "~/shared/experiments/checkout/deliveryDates";
import { useEffect, useState } from "react";

import {
  SHIPPING_METHOD_STANDARD,
  SHIPPING_METHOD_TWO_DAY,
} from "~/shared/sharedConstants";
import KickClient from "~/visits/utils/api.js";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export function formatShippingDate(date) {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== date
  ) {
    return "";
  }
  return dateFormatter.format(parsed);
}

export function formatDeliveryDates(estimate) {
  if (!estimate?.available) return "";
  const start = formatShippingDate(estimate.deliveryStartDate);
  const end = formatShippingDate(estimate.deliveryEndDate);
  if (!start || !end || estimate.deliveryStartDate > estimate.deliveryEndDate) {
    return "";
  }
  return start === end ? start : `${start} – ${end}`;
}

export function formatDeliveryWindow(estimate) {
  const dates = formatDeliveryDates(estimate);
  return dates ? `Estimated arrival: ${dates}` : "";
}

const shippingMethods = [SHIPPING_METHOD_STANDARD, SHIPPING_METHOD_TWO_DAY];

export default function useCheckoutShippingEstimates({
  enabled,
  checkoutSessionID,
  visitID,
  orderType,
  usState,
  zip,
  poBox,
}) {
  const [result, setResult] = useState(null);
  const selection = { orderType, usState, zip, poBox };
  const selectionJSON = JSON.stringify(selection);
  const key = `${visitID}:${selectionJSON}`;
  // Transit estimates use the state, so they can load before ZIP entry.
  const eligible = enabled && !!visitID && !!usState;

  useEffect(() => {
    if (!eligible) return;
    let canceled = false;
    let timer;
    const controller = new AbortController();
    const client = KickClient.newAuthenticatedClient();
    const request = JSON.parse(selectionJSON);
    const path = `/visits/${visitID}/shipping-estimates`;

    async function refresh() {
      setResult(null);
      let refreshAt = Date.now() + 60000;
      try {
        const { data } = await client.post(path, request, {
          signal: controller.signal,
          timeout: 10000,
        });
        if (canceled) return;
        trackDeliveryExperiment(
          checkoutSessionID,
          "Checkout Delivery Estimate Status",
          data?.[SHIPPING_METHOD_STANDARD]?.available
            ? "available"
            : "unavailable",
        );
        // Replace and expire the pair together, including across cutoffs.
        const expiresAt = Math.min(
          ...shippingMethods.map((method) =>
            Date.parse(data?.[method]?.expiresAt),
          ),
        );
        if (expiresAt > Date.now()) {
          refreshAt = expiresAt;
          const estimates = Object.fromEntries(
            shippingMethods.map((method) => [
              method,
              formatDeliveryWindow(data[method]) ? data[method] : null,
            ]),
          );
          setResult({ key, estimates, expiresAt });
        }
      } catch {
        // The cleanup aborts an in-flight refresh when the destination changes
        // or the patient leaves the step. That is our own cancellation, not a
        // lookup failure, so it must not count as one.
        if (canceled) return;
        trackDeliveryExperiment(
          checkoutSessionID,
          "Checkout Delivery Estimate Status",
          "request_failed",
        );
        // Estimates are advisory; a failed lookup must never block checkout.
      }
      if (!canceled) {
        timer = setTimeout(refresh, Math.max(1, refreshAt - Date.now()));
      }
    }

    // Avoid requests for every keystroke while the destination is being edited.
    timer = setTimeout(refresh, 300);
    return () => {
      canceled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [eligible, visitID, selectionJSON, key, checkoutSessionID]);

  const estimates =
    eligible && result?.key === key && result.expiresAt > Date.now()
      ? result.estimates
      : null;

  useEffect(() => {
    if (!estimates) return;
    // Record only quotes rendered for the current destination and carrier.
    const client = KickClient.newAuthenticatedClient();
    for (const shipmentMethod of shippingMethods) {
      const estimate = estimates[shipmentMethod];
      if (!estimate) continue;
      client
        .post(`/visits/${visitID}/shipping-estimate/shown`, {
          ...JSON.parse(selectionJSON),
          shipmentMethod,
          estimate,
        })
        .catch(() => {});
    }
  }, [estimates, visitID, selectionJSON]);

  return {
    estimates: estimates || {},
    deliveryWindows: Object.fromEntries(
      shippingMethods.map((method) => [
        method,
        formatDeliveryWindow(estimates?.[method]),
      ]),
    ),
  };
}
